import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { SaktiAppConfigApiService } from '../services/saktiAppConfigApiService';
import {
  CreateAppConfigPayload,
  CreateFeatureFlagPayload,
  SaktiAppConfigItem,
  SaktiFeatureFlagAdminItem,
  UpdateAppConfigPayload,
  UpdateFeatureFlagPayload,
} from '../types';

interface UseSaktiAppConfigResult {
  appConfigs: SaktiAppConfigItem[];
  featureFlags: SaktiFeatureFlagAdminItem[];
  loading: boolean;
  fetchAll: () => Promise<void>;
  createConfig: (payload: CreateAppConfigPayload) => Promise<boolean>;
  updateConfig: (id: string, payload: UpdateAppConfigPayload) => Promise<boolean>;
  deleteConfig: (id: string) => Promise<boolean>;
  createFlag: (payload: CreateFeatureFlagPayload) => Promise<boolean>;
  updateFlag: (id: string, payload: UpdateFeatureFlagPayload) => Promise<boolean>;
  deleteFlag: (id: string) => Promise<boolean>;
}

export const useSaktiAppConfig = (): UseSaktiAppConfigResult => {
  const toast = useToast();
  const [appConfigs, setAppConfigs] = useState<SaktiAppConfigItem[]>([]);
  const [featureFlags, setFeatureFlags] = useState<SaktiFeatureFlagAdminItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, ffRes] = await Promise.all([
        SaktiAppConfigApiService.listAppConfigs(),
        SaktiAppConfigApiService.listFeatureFlags(),
      ]);
      setAppConfigs(cfgRes.data ?? []);
      setFeatureFlags(ffRes.data ?? []);
    } catch (error) {
      toast({
        title: 'Gagal memuat data',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const createConfig = useCallback(async (payload: CreateAppConfigPayload) => {
    try {
      await SaktiAppConfigApiService.createAppConfig(payload);
      toast({ title: 'App config berhasil dibuat', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal membuat app config',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  const updateConfig = useCallback(async (id: string, payload: UpdateAppConfigPayload) => {
    try {
      await SaktiAppConfigApiService.updateAppConfig(id, payload);
      toast({ title: 'App config berhasil diupdate', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal update app config',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  const deleteConfig = useCallback(async (id: string) => {
    try {
      await SaktiAppConfigApiService.deleteAppConfig(id);
      toast({ title: 'App config berhasil dihapus', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal menghapus app config',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  const createFlag = useCallback(async (payload: CreateFeatureFlagPayload) => {
    try {
      await SaktiAppConfigApiService.createFeatureFlag(payload);
      toast({ title: 'Feature flag berhasil dibuat', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal membuat feature flag',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  const updateFlag = useCallback(async (id: string, payload: UpdateFeatureFlagPayload) => {
    try {
      await SaktiAppConfigApiService.updateFeatureFlag(id, payload);
      toast({ title: 'Feature flag berhasil diupdate', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal update feature flag',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  const deleteFlag = useCallback(async (id: string) => {
    try {
      await SaktiAppConfigApiService.deleteFeatureFlag(id);
      toast({ title: 'Feature flag berhasil dihapus', status: 'success', duration: 3000, isClosable: true });
      await fetchAll();
      return true;
    } catch (error) {
      toast({
        title: 'Gagal menghapus feature flag',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [fetchAll, toast]);

  return {
    appConfigs,
    featureFlags,
    loading,
    fetchAll,
    createConfig,
    updateConfig,
    deleteConfig,
    createFlag,
    updateFlag,
    deleteFlag,
  };
};

export default useSaktiAppConfig;
